import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Avatar, Button, CircularProgress, Grid, Paper, Container } from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../components/Auth';
import Feed from '../components/Feed';
import EventsCalendar from '../components/EventsCalendar';
import MediaTextView from '../components/MediaTextView';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    let d;
    if (ts.toDate) d = ts.toDate();
    else if (ts.seconds) d = new Date(ts.seconds * 1000);
    else d = new Date(ts);
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) {
    return '';
  }
}

export default function ChannelPage() {
  const { channelId } = useParams();
  const { authUser, firestoreInstance } = useContext(AuthContext);
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function fetchChannel() {
      if (!firestoreInstance || !channelId) return;
      setLoading(true);
      try {
        const d = await getDoc(doc(firestoreInstance, 'channels', channelId));
        if (!d.exists()) {
          if (mounted) {
            setError('Channel not found');
            setChannel(null);
            setLoading(false);
          }
          return;
        }
        const data = d.data();
        if (mounted) setChannel({ id: d.id, ...data });
        // check following state
        if (authUser?.user?.uid) {
          const f = await getDoc(doc(firestoreInstance, 'channels', channelId, 'followers', authUser.user.uid));
          if (mounted) setIsFollowing(!!f.exists());
        }
        if (mounted) setLoading(false);
      } catch (err) {
        console.error(err);
        if (mounted) {
          setError('Failed to load channel');
          setLoading(false);
        }
      }
    }
    fetchChannel();
    return () => { mounted = false; };
  }, [firestoreInstance, channelId, authUser]);

  const toggleFollow = async () => {
    if (!authUser?.user) {
      alert('Please sign in to follow channels.');
      return;
    }
    const uid = authUser.user.uid;
    try {
      if (!isFollowing) {
        await setDoc(doc(firestoreInstance, 'channels', channelId, 'followers', uid), {
          uid,
          displayName: authUser.user.displayName || '',
          photoURL: authUser.user.photoURL || '',
          createdAt: serverTimestamp()
        });
        setIsFollowing(true);
      } else {
        await deleteDoc(doc(firestoreInstance, 'channels', channelId, 'followers', uid));
        setIsFollowing(false);
      }
    } catch (err) {
      console.error('follow toggle failed', err);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ mt: 4 }}><Typography color="error">{error}</Typography></Container>;
  if (!channel) return null;

  const heroImage = channel.image_url || channel.coverImageUrl || channel.creator_info?.profileImageUrl || '';
  const creator = channel.creator_info || {};

  return (
    <Box>
      <Box sx={{ position: 'relative', height: { xs: 220, md: 300 }, bgcolor: '#111', color: '#fff', mb: 2 }}
           style={{ backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: heroImage ? `url(${heroImage})` : 'none' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.6))' }} />
        <Container sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'flex-end', pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={creator.profileImageUrl || channel.profileImageUrl || ''} sx={{ width: 80, height: 80 }} />
            <Box>
              <Typography variant="h5" sx={{ color: '#fff' }}>{channel.title || 'Channel'}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>{creator.displayName || creator.email || ''} · {channel.followersCount || 0} followers · {formatTimestamp(channel.created_at)}</Typography>
            </Box>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Button variant={isFollowing ? 'outlined' : 'contained'} onClick={toggleFollow} sx={{ color: isFollowing ? '#fff' : undefined }}>
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
          </Box>
        </Container>
      </Box>

      <Container>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>{channel.title}</Typography>
              {channel.description && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{channel.description}</Typography>}
            </Paper>

            <Feed channelId={channelId} channelOwnerId={channel.user_id || channel.ownerId || ''} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>About</Typography>
              <Box>
                <Typography variant="body2">{creator.displayName || creator.email || 'Creator'}</Typography>
                <Typography variant="caption" color="text.secondary">Joined: {formatTimestamp(channel.created_at)}</Typography>
              </Box>
            </Paper>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Events</Typography>
              <EventsCalendar channelId={channelId} isOwner={authUser?.user?.uid === channel.user_id} />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Media</Typography>
              <MediaTextView
                channelId={channelId}
                currentUserId={authUser?.user?.uid}
                isOwner={authUser?.user?.uid === channel.user_id}
                inputOnTop={false}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
